import { lighten } from '@mui/system'
import React from 'react'

import Icon from 'components/Icon'
import { ConcurrentTree } from 'components/scenarios/concurrent/ConcurrentTree'
import {
  ActiveNodeIcon,
  ConcurrentIcon,
  StatusChecksIcon
} from 'components/scenarios/concurrent/ConcurrentTreeIcons'
import { ConcurrentTreeItem } from 'components/scenarios/concurrent/ConcurrentTreeItem'
import { ConcurrentTreeNodesRoot } from 'components/scenarios/concurrent/ConcurrentTreeNodesRoot'
import { StatusCheckGraphNode } from 'components/scenarios/concurrent/StatusCheckGraphNode'
import {
  getColorByState,
  getLifecycleColor,
  isActive,
  isFailed,
  isHalted,
  isHaltRequested,
  isNotStarted,
  isSuccessful
} from 'components/scenarios/utils/graph-node-lifecycle'
import { useTranslation } from 'hooks/use-translation'
import theme from 'theme'
import { type ScenarioRunGraph } from 'types/api/scenarios/runs/scenario-runs'
import {
  type ContinuousStatusCheckNode,
  type ScenarioGraph,
  ScenarioLifecycle,
  type GraphNode
} from 'types/api/scenarios/scenario'

/**
 * Find the most relevant lifecycle for the overall list of observers
 * Goes in order of priority.
 * E.g., if any have halted or failed, then we display an error state
 */
function getOverallObserverLifecycle(
  observers: ContinuousStatusCheckNode[]
): ScenarioLifecycle {
  let lifecycle: ScenarioLifecycle = ScenarioLifecycle.NotStarted

  for (const observer of observers) {
    if (isHalted(observer.state?.lifecycle)) {
      lifecycle = ScenarioLifecycle.Halted
      break
    }

    if (isHaltRequested(observer.state?.lifecycle)) {
      lifecycle = ScenarioLifecycle.HaltRequested
      break
    }

    if (isFailed(observer.state?.lifecycle)) {
      lifecycle = ScenarioLifecycle.Failed
      break
    }

    if (isActive(observer.state?.lifecycle)) {
      lifecycle = ScenarioLifecycle.Active
      break
    }

    if (isSuccessful(observer.state?.lifecycle)) {
      lifecycle = ScenarioLifecycle.Successful
      break
    }
  }

  return lifecycle
}

export const ConcurrentObserversTreeView: React.FC<{
  graph: ScenarioGraph | ScenarioRunGraph
  onClick?: (node: GraphNode) => void
  isClickable?: boolean
}> = ({ graph, onClick = () => null, isClickable = false, ...rest }) => {
  const { trans } = useTranslation()

  const lifecycle = getOverallObserverLifecycle(graph.observers)
  const overallStatusColor = getLifecycleColor(lifecycle)
  const overallStatusBgColor = lighten(overallStatusColor, 0.9)

  return (
    <ConcurrentTree
      expanded={['1']} // lock the tree view open at the root node
      {...rest}
    >
      <ConcurrentTreeNodesRoot
        icon={
          <StatusChecksIcon
            css={{
              border: `1px solid ${overallStatusColor}`,
              background: overallStatusBgColor
            }}
          />
        }
        label={trans('status_checks')}
        nodeId="1"
      >
        {graph.observers.map((observer) => {
          const isNodeActive = isActive(observer.state?.lifecycle)
          const isNodeNotStarted = isNotStarted(observer.state?.lifecycle)
          const statusColor = isNodeNotStarted
            ? getLifecycleColor(ScenarioLifecycle.NotStarted)
            : getColorByState(observer)
          const statusBgColor = lighten(statusColor, 0.9)

          return (
            <ConcurrentTreeItem
              key={observer.guid}
              endIcon={
                isNodeActive ? (
                  <ActiveNodeIcon
                    css={{
                      border: `1px solid ${statusColor}`,
                      background: statusBgColor
                    }}
                  />
                ) : (
                  <ConcurrentIcon
                    css={{
                      border: `1px solid ${statusColor}`,
                      background: statusBgColor
                    }}
                  />
                )
              }
              label={
                <StatusCheckGraphNode
                  isClickable={isClickable}
                  node={observer}
                  rowActions={
                    isClickable ? (
                      <Icon
                        css={{
                          marginRight: theme.space[4],
                          marginLeft: theme.space[4]
                        }}
                        icon="chevronRight"
                      />
                    ) : null
                  }
                  onClick={onClick}
                />
              }
              nodeId={observer.guid}
            />
          )
        })}
      </ConcurrentTreeNodesRoot>
    </ConcurrentTree>
  )
}
